import { useEffect, useMemo, useState } from 'react';

import { mediaQuerySizes, ScreenReaderOnly, SimpleSlider, Typography } from '@capsule/consumer-design-system';

import pluralize from 'pluralize';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { SearchBar } from 'src/app/components/Elements/SearchBar';
import Spinner from 'src/app/components/Elements/Spinner';
import { TrailingTransparentGradient } from 'src/app/components/Elements/ToastBanner/styles';
import { AddonOTCItem } from 'src/app/components/Pages/Essentials/CheckoutAddons/AddonOTCItem';
import { checkoutAddonSearch } from 'src/app/components/Pages/Essentials/CheckoutAddons/checkoutAddonSearch';
import { useOTCMedCheckoutAddonsV2 } from 'src/app/hooks/data/otc';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { checkout, medications } from 'src/app/router/paths';
import { getCartItemQuantity } from 'src/app/state/selectors';
import { useSelector } from 'src/app/state/store';
import { SESSION_STORAGE_KEYS } from 'src/app/storage/sessionStorageKeys';
import type { OTCCategoryV2, OTCItemV2 } from 'src/app/types';

const Page = styled.div`
  --upsellMaxWidth: 756px;

  color: ${({ theme }) => theme.colors.capsuleBlue50};
  display: flex;
  flex-direction: column;
  align-items: center;

  ${({ theme }) => theme.mediaQueries.medium} {
    background-color: ${({ theme }) => theme.colors.capsuleGreen00};
    margin: 0;
    padding: ${({ theme }) => theme.space.s6} 0;
  }
  ${({ theme }) => theme.mediaQueries.mediumDown} {
    background-color: ${({ theme }) => theme.colors.white};
  }
`;

const PageTitle = styled(Typography).attrs({ variant: 'heading2', as: 'h2' })`
  align-self: flex-start;

  font-size: 24px !important;
`;

const CardListItem = styled.li<{ hasCarouselSlides?: boolean }>`
  gap: ${({ theme }) => theme.space.s4};
  height: 335px;
  border-radius: ${({ theme }) => theme.radii.r100};
  color: ${({ theme }) => theme.colors.capsuleBlue50};
  background-color: ${({ theme }) => theme.colors.white};
  border-top: solid 1px ${({ theme }) => theme.colors.capsuleGreen00};

  ${({ theme }) => theme.mediaQueries.small} {
    padding: ${({ theme }) => theme.space.s5};
  }
  ${({ theme }) => theme.mediaQueries.smallDown} {
    padding: ${({ theme }) => `${theme.space.s5} 0 ${theme.space.s5} ${theme.space.s5}`};
  }
`;

const SearchBarContainer = styled.div`
  position: relative;
  padding: ${({ theme }) => theme.space.s5};
`;

const CheckoutCTAContainer = styled.div`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 3;
`;

const CheckoutCTA = styled.div`
  position: relative;
  height: ${({ theme }) => theme.space.s7};
  width: calc(100% - ${({ theme }) => theme.space.s5});
  border-radius: ${({ theme }) => theme.radii.r100};
  background-color: ${({ theme }) => theme.colors.capsuleBlue50};
  color: ${({ theme }) => theme.colors.white} !important;
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;
  font-weight: 600;
  max-width: 534px;

  ${({ theme }) => theme.mediaQueries.small} {
    width: var(--upsellMaxWidth);
  }
`;

const BagCountBadge = styled.div`
  position: absolute;
  right: ${({ theme }) => theme.space.s3};
  top: 50%;
  transform: translateY(-50%);
  height: 3rem;
  width: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${({ theme }) => theme.colors.capsuleBlue50};

  & img {
    width: 100%;
    height: 100%;
  }

  & > span {
    position: absolute;
    top: 25%;
  }
`;

const SearchResultsContainer = styled.div`
  background-color: white;

  align-items: center;
  justify-content: space-around;

  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(162px, 162px));
  grid-gap: 5px;

  border-top: solid 1px ${({ theme }) => theme.colors.capsuleGreen00};
  padding: ${({ theme }) => theme.space.s4};

  ${({ theme }) => theme.mediaQueries.mediumDown} {
    grid-template-columns: repeat(auto-fit, minmax(162px, 1fr));
  }
`;

const moneyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

export const CheckoutAddons = () => {
  const [hasTrackedViewEvent, setHasTrackedViewEvent] = useState(false);
  const { data: allOtcCategories, isLoading, isFetched } = useOTCMedCheckoutAddonsV2();
  const trackViewCheckoutAddOnsEvent = useSegmentEvent('viewCheckoutAddOns', { shouldTrackOnMount: false });
  const trackSearchEvent = useSegmentEvent('search', { shouldTrackOnMount: false });
  const trackScrollEvent = useSegmentEvent('modalCategoryScroll', { shouldTrackOnMount: false });
  const trackAddToCartEvent = useSegmentEvent('addToCart', { shouldTrackOnMount: false });
  const trackRemoveFromCartEvent = useSegmentEvent('removeFromCart', { shouldTrackOnMount: false });

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      window.sessionStorage.setItem(SESSION_STORAGE_KEYS.seenUpsellEssentials, 'true');
    }
  }, []);

  useEffect(() => {
    if (!isLoading && isFetched && !hasTrackedViewEvent) {
      trackViewCheckoutAddOnsEvent({ otc_count: allOtcCategories?.length ?? 0 });
      setHasTrackedViewEvent(true);
    }
  }, [allOtcCategories?.length, hasTrackedViewEvent, isFetched, isLoading, trackViewCheckoutAddOnsEvent]);

  const cartItemQuantity = useSelector(getCartItemQuantity);

  const [isSearching, setIsSearching] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState<OTCCategoryV2[]>([]);

  const renderedCategories = isSearching ? filteredCategories : allOtcCategories;
  const flatSearchedItems = useMemo(
    () => filteredCategories.map(category => category.items).flat(),
    [filteredCategories]
  );

  return (
    <Page>
      {isLoading || !allOtcCategories ? (
        <Spinner centered />
      ) : (
        <div
          css={css`
            position: relative;
            padding-bottom: ${({ theme }) => theme.space.s8};
            width: 100%;
            max-width: var(--upsellMaxWidth);
          `}
        >
          <div
            css={css`
              ${({ theme }) => theme.mediaQueries.mediumDown} {
                padding: ${({ theme }) => theme.space.s5};
                padding-bottom: 0;
              }
            `}
          >
            <PageTitle>Want to add anything else?</PageTitle>
          </div>

          <div
            css={css`
              background-color: ${({ theme }) => theme.colors.white};
            `}
          >
            <SearchBarContainer>
              <SearchBar
                handleChange={e => {
                  const searchText = e.currentTarget.value;
                  setIsSearching(!!searchText);
                  checkoutAddonSearch({ searchText, allOtcCategories, filteredCategories, setFilteredCategories });
                }}
                handleBlur={e => {
                  const search_term = e.currentTarget.value;
                  trackSearchEvent({ search_term, page: 'add_ons' });
                }}
              />
            </SearchBarContainer>

            {isSearching && (!filteredCategories || filteredCategories.length === 0) && (
              <div>
                <CardListItem as="div">
                  <Typography variant="body">No results for your search.</Typography>
                </CardListItem>
              </div>
            )}

            {isSearching && (
              <SearchResultsContainer>
                {flatSearchedItems?.map((item: OTCItemV2, itemIndex: number) => {
                  const formattedPrice = moneyFormatter.format(item.price);
                  const analyticsCartManipulationPayload = {
                    display_name_primary: item.name,
                    dosage: item.dosage,
                    price: formattedPrice,
                    quantity: item.quantity ?? 1,
                    OTC: true,
                    patientmedication_id: null,
                    prescription_fulfillment_id: null,
                    page: 'add_ons',
                  };

                  const position = [1, itemIndex + 1];

                  return (
                    <AddonOTCItem
                      key={item.id}
                      {...item}
                      formattedPrice={formattedPrice}
                      // TODO: Analytics updates with new UI?
                      onAddToCart={() => trackAddToCartEvent({ ...analyticsCartManipulationPayload, position })}
                      onRemoveFromCart={() => trackRemoveFromCartEvent(analyticsCartManipulationPayload)}
                    />
                  );
                })}
              </SearchResultsContainer>
            )}

            {!isSearching && renderedCategories && renderedCategories?.length > 0 && (
              <ol
                css={css`
                  list-style: none;
                `}
              >
                {renderedCategories.map((category, categoryIndex) => {
                  return (
                    category.items.length > 0 && (
                      <CardListItem key={category.name} hasCarouselSlides={category.items.length > 3}>
                        <Typography variant="heading4">{category.name}</Typography>
                        <div
                          css={css`
                            margin-top: ${({ theme }) => theme.space.s4};
                          `}
                        >
                          <SimpleSlider
                            isPaginationCircleVisible={true}
                            isNavigationButtonVisible={true}
                            breakpoints={{
                              1: {
                                isLooped: false,
                                slidesPerView: 2.2,
                                slidesPerGroup: 1,
                                spaceBetweenSlides: 0,
                                isPaginationCircleVisible: false,
                                isNavigationButtonVisible: false,
                              },
                              440: {
                                isLooped: false,
                                slidesPerView: 3.2,
                                slidesPerGroup: 1,
                                spaceBetweenSlides: 0,
                                isPaginationCircleVisible: false,
                                isNavigationButtonVisible: false,
                              },
                              [mediaQuerySizes.small]: {
                                isLooped: category.items.length / 3 > 1,
                                slidesPerView: 3,
                                slidesPerGroup: 3,
                                spaceBetweenSlides: 0,
                                isPaginationCircleVisible: true,
                                isNavigationButtonVisible: true,
                              },
                              [mediaQuerySizes.medium]: {
                                isLooped: category.items.length / 5 > 1,
                                slidesPerView: 5,
                                slidesPerGroup: 5,
                                spaceBetweenSlides: 0,
                                isPaginationCircleVisible: true,
                                isNavigationButtonVisible: true,
                              },
                            }}
                            slides={category.items.map((item, itemIndex) => {
                              const formattedPrice = moneyFormatter.format(item.price);

                              const analyticsCartManipulationPayload = {
                                display_name_primary: item.name,
                                dosage: item.dosage,
                                price: formattedPrice,
                                quantity: item.quantity ?? 1,
                                OTC: true,
                                patientmedication_id: null,
                                prescription_fulfillment_id: null,
                                page: 'add_ons',
                              };

                              const position = [categoryIndex + 1, itemIndex + 1];

                              return (
                                <AddonOTCItem
                                  key={item.id}
                                  {...item}
                                  formattedPrice={formattedPrice}
                                  // TODO: Analytics updates with new UI?
                                  onAddToCart={() =>
                                    trackAddToCartEvent({ ...analyticsCartManipulationPayload, position })
                                  }
                                  onRemoveFromCart={() => trackRemoveFromCartEvent(analyticsCartManipulationPayload)}
                                />
                              );
                            })}
                            onNextPageCallback={() => {
                              trackScrollEvent({ category: category.name, horizontal_scroll: true, carousel: 'right' });
                            }}
                            onPrevPageCallback={() => {
                              trackScrollEvent({ category: category.name, horizontal_scroll: true, carousel: 'left' });
                            }}
                            onPageUpdateCallback={() => {
                              //trackScrollEvent({ category: category.name, horizontal_scroll: true, carousel: 'left' });
                            }}
                          />
                        </div>
                      </CardListItem>
                    )
                  );
                })}
              </ol>
            )}
          </div>
        </div>
      )}

      <CheckoutCTAContainer>
        {cartItemQuantity > 0 ? (
          <CheckoutCTA as={Link} to={checkout.route}>
            <Typography variant="callout">Continue To Checkout</Typography>

            <BagCountBadge>
              <Typography variant="heading4" as="span" aria-live="polite">
                {cartItemQuantity}
                <ScreenReaderOnly>{`${pluralize('item', cartItemQuantity)} in cart.`}</ScreenReaderOnly>
              </Typography>

              <img src="/public/Bag.svg" alt="" />
            </BagCountBadge>
          </CheckoutCTA>
        ) : (
          <CheckoutCTA as={Link} to={medications.route}>
            <Typography variant="callout">Go Home</Typography>
          </CheckoutCTA>
        )}

        <TrailingTransparentGradient aria-hidden="true" />
      </CheckoutCTAContainer>
    </Page>
  );
};
